/**
 * @file     VerdictCard.tsx
 * @purpose  Hero verdict card: large plain-English headline, status badge, and a horizontal key-value strip of supporting net worth + cash-out metrics with animated values.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAnimatedNumber } from '@/hooks/useAnimatedNumber';
import type { RentVsBuyResult, RentVsBuyInputs } from './rentVsBuyMath';

type Props = {
  inputs: RentVsBuyInputs;
  result: RentVsBuyResult;
  loading?: boolean;
};

const fmtCAD0 = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(
    Number.isFinite(n) ? n : 0,
  );

export const VerdictCard = ({ inputs, result, loading = false }: Props) => {
  const { theme } = useAppTheme();

  const animatedYear = useAnimatedNumber(result.breakEvenYear ?? 0, 450);
  const animatedBuyer = useAnimatedNumber(result.buyerNetWorthAtHorizon, 450);
  const animatedRenter = useAnimatedNumber(result.renterNetWorthAtHorizon, 450);
  const animatedGap = useAnimatedNumber(
    result.buyerNetWorthAtHorizon - result.renterNetWorthAtHorizon,
    450,
  );
  const animatedBuyerCash = useAnimatedNumber(result.buyerCumCostAtHorizon, 450);
  const animatedRenterCash = useAnimatedNumber(result.renterCumCostAtHorizon, 450);

  const buyerWins = result.buyerWins;
  const tone = buyerWins ? 'success' : 'info';
  const toneRaw = buyerWins ? theme.rawSemanticColors.statusSuccess : theme.rawSemanticColors.statusInfo;
  const toneFg = buyerWins ? theme.semanticColors.statusSuccess : theme.semanticColors.statusInfo;
  const badgeBg = alpha(toneRaw, 0.1);
  const badgeLabel = buyerWins ? 'Buying wins' : 'Renting wins';

  const yearText = buyerWins
    ? `${Math.max(1, Math.round(animatedYear))} year${result.breakEvenYear === 1 ? '' : 's'}`
    : `year ${inputs.horizonYears}`;

  const subtext = buyerWins
    ? "If you'll stay in the home longer than that, buying builds more net worth than renting and investing the difference."
    : "Within the time horizon you picked, renting and investing the difference comes out ahead.";

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: theme.semanticColors.surfaceCard,
        borderRadius: `${theme.layout.radius.md}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        overflow: 'hidden',
        p: { xs: 2.5, sm: 3, md: 3.5 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `linear-gradient(180deg, ${alpha(toneRaw, 0.05)} 0%, ${alpha(toneRaw, 0)} 60%)`,
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: `${theme.layout.radius.pill}px`,
              bgcolor: badgeBg,
              color: toneFg,
              fontSize: theme.typography.bodySm,
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: '0.01em',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: toneFg,
                display: 'inline-block',
              }}
            />
            {badgeLabel}
          </Box>
        </Box>

        {loading ? (
          <Skeleton variant="text" width="70%" height={48} sx={{ mb: 1 }} />
        ) : (
          <Typography
            component="h2"
            sx={{
              fontFamily: theme.typography.fontFamilyHeading,
              fontSize: { xs: theme.typography.h3, md: theme.typography.h2 },
              fontWeight: 600,
              color: theme.semanticColors.textPrimary,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            {buyerWins ? 'Buying wins after ' : 'Renting wins through '}
            <Box component="span" sx={{ color: toneFg, fontVariantNumeric: 'tabular-nums' }}>
              {yearText}
            </Box>
            .
          </Typography>
        )}
        <Typography sx={{ color: theme.semanticColors.textSecondary, fontSize: theme.typography.bodyMd, mb: 2.5, maxWidth: 640 }}>
          {subtext}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            rowGap: 2,
            columnGap: 0,
            pt: 2.5,
            borderTop: `1px solid ${theme.semanticColors.divider}`,
          }}
        >
          <Stat
            label={`Buyer net worth at year ${inputs.horizonYears}`}
            value={fmtCAD0(animatedBuyer)}
            loading={loading}
            tone={tone === 'success' ? 'success' : undefined}
            divider
          />
          <Stat
            label={`Renter net worth at year ${inputs.horizonYears}`}
            value={fmtCAD0(animatedRenter)}
            loading={loading}
            tone={tone === 'info' ? 'info' : undefined}
            divider
          />
          <Stat
            label="Net worth gap"
            value={`${animatedGap >= 0 ? '+' : '-'}${fmtCAD0(Math.abs(animatedGap)).replace(/^-/, '')}`}
            loading={loading}
            divider
          />
          <Stat
            label={`Total cash out (buy / rent)`}
            value={`${fmtCAD0(animatedBuyerCash)} / ${fmtCAD0(animatedRenterCash)}`}
            valueScale="sm"
            loading={loading}
          />
        </Box>
      </Box>
    </Box>
  );
};

const Stat = ({
  label,
  value,
  tone,
  divider,
  loading,
  valueScale = 'lg',
}: {
  label: string;
  value: string;
  tone?: 'success' | 'info';
  divider?: boolean;
  loading?: boolean;
  valueScale?: 'lg' | 'sm';
}) => {
  const { theme } = useAppTheme();
  const valueColor =
    tone === 'success'
      ? theme.semanticColors.statusSuccess
      : tone === 'info'
      ? theme.semanticColors.statusInfo
      : theme.semanticColors.textPrimary;
  return (
    <Box
      sx={{
        position: 'relative',
        px: { xs: 0, md: 2 },
        '&:first-of-type': { pl: { md: 0 } },
        ...(divider
          ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '10%',
                bottom: '10%',
                right: 0,
                width: '1px',
                bgcolor: theme.semanticColors.divider,
                display: { xs: 'none', md: 'block' },
              },
            }
          : {}),
      }}
    >
      <Typography
        sx={{
          fontSize: theme.typography.bodySm,
          color: theme.semanticColors.textMuted,
          mb: 0.75,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width="60%" height={32} />
      ) : (
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: valueScale === 'lg' ? theme.typography.h4 : theme.typography.bodyLg,
            fontWeight: 600,
            color: valueColor,
            lineHeight: 1.15,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em',
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};
